'use client';

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useResendVerificationMutation } from "@/features/api/authApi";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";

export default function ResendVerification() {
  const [email, setEmail] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const { toast } = useToast();
  const [resendVerification, { isLoading }] =
    useResendVerificationMutation();

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const res = await resendVerification({ email }).unwrap();

      setModalMessage(
        res?.message || "Verification email sent successfully."
      );
      setIsModalOpen(true);
      setEmail("");

      toast({
        title: "Success",
        description:
          res?.message || "Verification email sent successfully.",
      });
    } catch (err) {
      toast({
        title: "Error",
        description:
          err?.data?.message || "Something went wrong.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex min-h-[65vh] items-center justify-center bg-background p-4 relative">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-2xl font-bold">
            Resend Verification Email
          </CardTitle>
        </CardHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <CardContent className="p-4 space-y-3">
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className="h-12 text-base"
              />
            </div>
          </CardContent>

          <CardFooter className="p-4">
            <Button
              type="submit"
              className="w-full h-12 text-base"
              disabled={isLoading}
            >
              {isLoading && (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              )}
              <Mail className="mr-2 h-5 w-5" />
              Send Verification Link
            </Button>
          </CardFooter>
        </form>
      </Card>

      {/* Success Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-lg text-center">
            <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-green-500" />
            <h2 className="text-xl font-bold mb-2">
              Email Sent Successfully!
            </h2>
            <p className="mb-4">{modalMessage}</p>
            <Button
              onClick={() => setIsModalOpen(false)}
              className="w-full"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}